/**
 * Sidebar/SetupMenu/index.tsx
 * desc: the Setup section of the v2 sidebar — the grouped configuration links, as an accordion while the sidebar is
 *       expanded (SetupAccordion) and as a flyout beside the rail while it is collapsed (SetupFlyout)
 * note: resolves only what both modes share — the groups the user may open, whether the current page belongs to
 *       Setup, the label — and picks the mode. Groups with nothing the user may open are dropped; with no groups
 *       left the whole section is.
 */

import { useLocation } from 'react-router';
import { translate } from '../../../utilities/localization';
import { SETUP_GROUPS, SETUP_HUBS, type NavItem } from '../config';
import { isNavItemActive, isUnderAppUrl } from '../utilities';
import SetupAccordion from './SetupAccordion';
import SetupFlyout from './SetupFlyout';

interface SetupMenuProps {
    isCollapsed: boolean;
    canAccess: (item: NavItem) => boolean;
}

const SetupMenu = ({ isCollapsed, canAccess }: SetupMenuProps) => {
    const { pathname } = useLocation();
    const label = translate('nav.link.setup');
    const groups = SETUP_GROUPS
        .map((group) => ({ ...group, items: group.items.filter(canAccess) }))
        .filter((group) => group.items.length > 0);
    const isActive = groups.some((group) => group.items.some((item) => isNavItemActive(item, pathname)))
        || SETUP_HUBS.some((key) => isUnderAppUrl(key, pathname));

    if (groups.length === 0) return null;

    return isCollapsed ?
        <SetupFlyout groups={ groups } isActive={ isActive } label={ label }/>
        :
        <SetupAccordion groups={ groups } isActive={ isActive } label={ label }/>;
};

export default SetupMenu;
